import { create } from "zustand";

interface ClientSelectionStore {
  // State
  selectedClientIds: Set<string>;

  // Selection actions
  toggleClient: (clientId: string, selected: boolean) => void;
  selectAll: (clientIds: string[]) => void;
  clearSelection: () => void;

  // Computed values
  isSelected: (clientId: string) => boolean;
  getSelectedCount: () => number;
  getSelectedArray: () => string[];
  isAllSelected: (availableClientIds: string[]) => boolean;
  isIndeterminate: (availableClientIds: string[]) => boolean;
}

/**
 * Store for managing client selection state in the client list table.
 *
 * Uses a Set for O(1) lookup performance and automatic deduplication.
 * Selection state is scoped to this store and will persist across
 * renders but can be cleared when filters/pages change.
 *
 * @example
 * ```tsx
 * const { toggleClient, isSelected, clearSelection } = useClientSelectionStore();
 *
 * // Toggle a client
 * toggleClient(clientId, true);
 *
 * // Check if selected
 * const selected = isSelected(clientId);
 *
 * // Clear all
 * clearSelection();
 * ```
 */
export const useClientSelectionStore = create<ClientSelectionStore>(
  (set, get) => ({
    // Initial state
    selectedClientIds: new Set<string>(),

    /**
     * Toggle a client's selection state.
     * :param clientId: The client ID to toggle
     * :param selected: Whether the client should be selected
     */
    toggleClient: (clientId: string, selected: boolean) =>
      set((state) => {
        const newSet = new Set(state.selectedClientIds);
        if (selected) {
          newSet.add(clientId);
        } else {
          newSet.delete(clientId);
        }
        return { selectedClientIds: newSet };
      }),

    /**
     * Select all clients from the provided array.
     * Filters out null/undefined IDs automatically.
     * :param clientIds: Array of client IDs to select
     */
    selectAll: (clientIds: string[]) =>
      set({
        selectedClientIds: new Set(clientIds.filter((id) => id != null)),
      }),

    /**
     * Clear all selections.
     * Called when filters change, page changes, or bulk update completes.
     */
    clearSelection: () => set({ selectedClientIds: new Set<string>() }),

    /**
     * Check if a client is currently selected.
     * :param clientId: The client ID to check
     * :return: True if the client is selected
     */
    isSelected: (clientId: string) => {
      return get().selectedClientIds.has(clientId);
    },

    /**
     * Get the count of currently selected clients.
     * :return: Number of selected clients
     */
    getSelectedCount: () => {
      return get().selectedClientIds.size;
    },

    /**
     * Get an array of selected client IDs.
     * Useful for passing to bulk update operations.
     * :return: Array of selected client IDs
     */
    getSelectedArray: () => {
      return Array.from(get().selectedClientIds);
    },

    /**
     * Check if all available clients are selected.
     * Used for the "select all" checkbox checked state.
     * :param availableClientIds: Array of client IDs currently visible
     * :return: True if all available clients are selected
     */
    isAllSelected: (availableClientIds: string[]) => {
      if (availableClientIds.length === 0) return false;
      const selectedIds = get().selectedClientIds;
      return availableClientIds.every((id) => selectedIds.has(id));
    },

    /**
     * Check if some (but not all) available clients are selected.
     * Used for the "select all" checkbox indeterminate state.
     * :param availableClientIds: Array of client IDs currently visible
     * :return: True if some but not all clients are selected
     */
    isIndeterminate: (availableClientIds: string[]) => {
      if (availableClientIds.length === 0) return false;
      const selectedCount = get().selectedClientIds.size;
      if (selectedCount === 0) return false;

      // Check how many of the available clients are selected
      const selectedAvailableCount = availableClientIds.filter((id) =>
        get().selectedClientIds.has(id)
      ).length;

      return (
        selectedAvailableCount > 0 &&
        selectedAvailableCount < availableClientIds.length
      );
    },
  })
);
